import { Injectable, inject } from '@angular/core';

import Highcharts from 'highcharts';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { FormatService } from 'src/app/_core/format.service';
import { SettingsService } from 'src/app/_core/settings/settings.service';
import { TranslationsService } from 'src/app/_core/translations.service';
import { agrDefaultChartOptions } from 'src/app/item-card/chart/models/chart-options.model';
import { ItemCardService } from 'src/app/item-card/item-card.service';
import { ChartDataParams } from 'src/app/item-card/models/chart-data-params.model';
import {
  ChartElementTypeDTO,
  ItemCardDTO,
  SerieDataUpdateDTO,
  SerieDataUpdateResponseDTO
} from 'src/app/item-card/models/item-card-dto.model';

interface ItemCardChartData {
  options: Highcharts.Options;
  elementTypes: Map<string, ChartElementTypeDTO>;
}
/**
 * Highcharts 12 types series options as a union of every series type. The item card series are plain
 * series with { x, y } point objects, which this type describes.
 */
export type ItemCardSeriesOptions = Highcharts.SeriesOptionsType & { data?: Highcharts.PointOptionsObject[] };

@Injectable({
  providedIn: 'root'
})
export class ItemCardChartService {
  private itemCardService = inject(ItemCardService);
  private formatService = inject(FormatService);
  private settingsService = inject(SettingsService);

  getItemChartSeries(itemId: number, params: ChartDataParams, orderId?: number): Observable<ItemCardSeriesOptions[]> {
    return this.getItemChart(itemId, params, orderId).pipe(map((chart) => chart.options.series as ItemCardSeriesOptions[]));
  }

  getItemChart(itemId: number, params: ChartDataParams, orderId?: number): Observable<ItemCardChartData> {
    // @ts-ignore, getItemCardDto is private for a reason
    return this.itemCardService.getItemCardDto(itemId, params, orderId).pipe(
      map((itemDTO: ItemCardDTO) => {
        let chartOptions = this.getChartDefaultOptions(params);
        chartOptions = Highcharts.merge(chartOptions, itemDTO.chartOptions);
        const series = this.removeEmptySeries(this.getChartSeriesOptions(itemDTO));
        this.fixForecastDataPosition(series, params);
        this.fixSafetyStockForOrderPeriod(series, params);
        chartOptions.series = series;
        this.setMinMaxAxisValues(chartOptions);
        const elementTypes = new Map(itemDTO.chartElementTypes.map((elementType) => [elementType.name, elementType]));
        // Series x-values are UTC-midnight timestamps representing calendar-date buckets, so the axis must render them
        // in UTC. Reading the raw UTC fields shows the same calendar date for every viewer regardless of
        // their timezone offset, and UTC has no DST so the month/year labels stay correct. A previous fix applied
        // `getTimezoneOffset() + 1440` here, but that shifted the whole axis a full day for offsets >= 0 (DEV-13730).
        chartOptions.time = {
          timezone: 'UTC' // was useUTC: true (Highcharts 7); UTC is also Highcharts 12's default
        };
        return { elementTypes, options: chartOptions };
      })
    );
  }

  updateSerieData(
    itemId: number,
    params: ChartDataParams,
    chartElementId: number,
    serieDataUpdate: SerieDataUpdateDTO
  ): Observable<SerieDataUpdateResponseDTO> {
    return this.itemCardService.updateSerieData(itemId, params, chartElementId, serieDataUpdate);
  }

  private getChartDefaultOptions(chartParams: ChartDataParams): Highcharts.Options {
    const chartOptions = Highcharts.merge(Highcharts.defaultOptions, agrDefaultChartOptions);
    this.setChartPeriodTickInterval(chartOptions, chartParams);
    this.setForecastBackground(chartOptions, chartParams);
    return chartOptions;
  }

  /**
   * Finds the highcharts_series_options from the chart_elements database table in the DTO object
   * @param itemDTO DTO object from the database and API
   */
  private getChartSeriesOptions(itemDTO: ItemCardDTO): ItemCardSeriesOptions[] {
    return itemDTO.chartElementTypes.map((elementType) => {
      const series = elementType.highchartsSeriesOptions as ItemCardSeriesOptions;
      series.data = itemDTO.chartData
        .filter((data) => data.chart_element_id === elementType.id)
        .map((data) => {
          return { x: data.chart_date, y: data.value };
        });
      return series;
    });
  }

  private removeEmptySeries(series: ItemCardSeriesOptions[]): ItemCardSeriesOptions[] {
    return series.filter((serie) => serie.data.length > 0);
  }

  /**
   * Move the forecast data points for the current period (month or week) to the beginning of the period.
   * This logically stacks the adjusted sale and forecasted sale for the current period.
   */
  private fixForecastDataPosition(series: ItemCardSeriesOptions[], params: ChartDataParams): void {
    if (params.periodType === 'day') {
      return;
    }
    series
      .filter((serie) => ['forecast'].includes(serie.id))
      .map((serie) => {
        if (params.periodType === 'week') {
          serie.data
            .filter((point: Highcharts.PointOptionsObject) => this.isCurrentWeek(point.x as number))
            .map((point: Highcharts.PointOptionsObject) => (point.x = this.getStartOfWeekForDate(point.x as number).getTime()));
        } else if (params.periodType === 'month') {
          serie.data
            .filter((point: Highcharts.PointOptionsObject) => new Date(point.x).getUTCDate() !== 1)
            .map((point: Highcharts.PointOptionsObject) => (point.x = new Date(point.x).setUTCDate(1).valueOf()));
        }
      });
  }

  private isCurrentWeek(millisecondsDate: number): boolean {
    const date = new Date(millisecondsDate);
    const todayUtc = this.todayUtcMidnight();
    const currentWeekFirstDate = this.getStartOfWeekForDate(todayUtc);
    const nextWeekFirstDate = this.getStartOfWeekForDate(todayUtc + 604800000);
    return currentWeekFirstDate.getTime() <= date.getTime() && date.getTime() < nextWeekFirstDate.getTime();
  }

  // Chart x-values are UTC-midnight calendar dates (see item-card.service), so week math must use UTC date parts.
  private getStartOfWeekForDate(millisecondsDate: number): Date {
    const date = new Date(millisecondsDate);
    const dateOffset = (date.getUTCDay() + 7 - this.settingsService.startOfWeek()) % 7;
    return new Date(date.setUTCDate(date.getUTCDate() - dateOffset));
  }

  // UTC midnight of the client's current calendar date, matching the UTC-midnight bucket values on the axis.
  private todayUtcMidnight(): number {
    const now = new Date();
    return Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  }

  /* eslint-disable id-length */
  /**
   * Custom date format characters for the chart axis labels (%W, %y, %b, %d).
   */
  private setDateFormats(): void {
    // Set on the shared Highcharts instance (default import; the namespace import is read-only)
    // https://github.com/highcharts/highcharts/issues/9920#issuecomment-462272538
    Highcharts.dateFormats = {
      W: (timestamp) => {
        return `${TranslationsService.get('WEEK_SHORT')}${this.formatService.format(timestamp, 'date:ww')}`;
      },
      y: (timestamp) => this.formatService.format(timestamp, 'date:yy'),
      b: (timestamp) => this.formatService.format(timestamp, 'date:MMM'),
      d: (timestamp) => this.formatService.format(timestamp, 'date:fullDate')
    };
  }

  private setChartPeriodTickInterval(chartOptions: Highcharts.Options, chartParams: ChartDataParams): void {
    this.setDateFormats();
    const period = chartParams.periodType; // day, week or month
    const interval = { day: 1 * 24 * 3600 * 1000, week: 7 * 24 * 3600 * 1000, month: undefined };
    const xAxis: Highcharts.XAxisOptions =
      // Determines if xAxis is an array or just a single object
      chartOptions.xAxis && (chartOptions.xAxis as Highcharts.XAxisOptions[]).length ? chartOptions.xAxis[0] : chartOptions.xAxis;
    xAxis.startOfWeek = this.settingsService.startOfWeek();
    xAxis.gridLineWidth = 1;
    xAxis.tickPixelInterval = 30;
    xAxis.tickInterval = interval[period];
    xAxis.minTickInterval = 28 * 24 * 3600 * 1000;
    const weekFormat = this.settingsService.hideWeekNumber() ? "%e. %b'%y" : "%W - %b'%y";
    xAxis.dateTimeLabelFormats = {
      day: { main: '%e. %b' },
      week: { main: weekFormat },
      month: { main: "%b, '%y" }
    };
    chartOptions.tooltip.dateTimeLabelFormats.millisecond = this.settingsService.hideWeekNumber() ? '%d' : '%d, %W';
  }

  private setForecastBackground(options: Highcharts.Options, params: ChartDataParams): Highcharts.Options {
    if (params.forecastLength <= 0) {
      return {};
    }
    const now = new Date();
    const today = this.todayUtcMidnight();
    const endDate = {
      day: today + (params.forecastLength + 2) * 24 * 3600 * 1000,
      week: today + (params.forecastLength + 1) * 7 * 24 * 3600 * 1000,
      month: Date.UTC(now.getFullYear(), now.getMonth() + params.forecastLength + 1)
    };
    (options.xAxis as Highcharts.XAxisOptions).plotBands = [
      {
        color: '#EEEEEE',
        from: today,
        to: endDate[params.periodType]
      }
    ];
  }

  private fixSafetyStockForOrderPeriod(series: ItemCardSeriesOptions[], params: ChartDataParams): void {
    if (params.forecastLength <= 0) {
      return;
    }
    const safetyStockSeries = series.find((serie) => serie.id === 'safety_stock_for_order_period');
    if (!safetyStockSeries || !safetyStockSeries.data || !safetyStockSeries.data.length) {
      return;
    }
    const safetyStockValue = (safetyStockSeries.data[0] as Highcharts.PointOptionsObject).y;
    const now = new Date();
    const today = this.todayUtcMidnight();
    const endDate = {
      day: today + params.forecastLength * 24 * 3600 * 1000,
      week: today + params.forecastLength * 7 * 24 * 3600 * 1000 - now.getDay() * 24 * 3600 * 1000,
      month: Date.UTC(now.getFullYear(), now.getMonth() + params.forecastLength)
    };
    safetyStockSeries.data = [
      { x: today, y: safetyStockValue },
      { x: endDate[params.periodType], y: safetyStockValue }
    ];
  }

  private setMinMaxAxisValues(chartOptions: Highcharts.Options): void {
    const xAxis: Highcharts.XAxisOptions =
      // Determines if xAxis is an array or just a single object
      chartOptions.xAxis && (chartOptions.xAxis as Highcharts.XAxisOptions[]).length ? chartOptions.xAxis[0] : chartOptions.xAxis;
    const series = chartOptions.series as ItemCardSeriesOptions[];
    xAxis.max = Math.max(...series.map((serie) => Math.max(...serie.data.map((data) => data.x as number))));
    xAxis.min = Math.min(...series.map((serie) => Math.min(...serie.data.map((data) => data.x as number))));
  }
}
